/* Shared conversations store — single source of truth for
 * /api/messaging/conversations. Built on the generic createListStore
 * factory (same as casesStore, lawsStore, regulationsStore).
 *
 * ⚠️ Slight departure from the read-only sibling stores:
 *   Conversations are LIVE — an SSE event (message:new / conversation:new
 *   / conversation:read) means the list on disk is now stale. The
 *   caller invalidates via `invalidateConversations()`, ideally behind
 *   a 500 ms debounce so a burst of events (5 messages in the same
 *   group in one second) results in ONE refetch instead of five.
 *   See MessagesList.jsx for the debouncer.
 *
 * TTL kept at 30 s to match the sibling stores; SSE invalidation makes
 * this a soft ceiling rather than a hard cache expiry.
 */
import { listConversations } from "./messagingApi";
import { createListStore } from "./createListStore";

const store = createListStore({
  fetcher: listConversations,
  staleMs: 30_000,
  name: "conversations",
});

export const fetchConversations = store.fetch;
export const prefetchConversations = store.prefetch;
export const invalidateConversations = store.invalidate;

/* Instant in-memory patch — called from the SSE handler so the incoming
 * message preview lands in the card the moment the frame arrives, not
 * after a debounced full-list refetch (which is still scheduled for
 * consistency but is no longer on the critical path).
 *
 * Merge rules:
 *   • Bump the matched row to the top (mimics WhatsApp/iMessage)
 *   • Update `last_message_preview`, `last_message_at`, `last_sender_id`
 *   • Increment `unread` when the message is from the peer (not us)
 *   • No-op if the conversation isn't in the current list snapshot
 *     (edge case: first ever message on a brand-new conv — the trailing
 *     debounced refetch will pick that up). */
export function patchConversationOnMessage({ myId, event }) {
  const convId = event?.conversation_id;
  const msg = event?.message;
  if (!convId || !msg) return;
  const isMine = msg.sender_id === myId;
  store.patch((rows) => {
    const idx = rows.findIndex((r) => r.id === convId);
    if (idx === -1) return rows;
    const prev = rows[idx];
    const bodyPreview = (msg.body || "").replace(/\s+/g, " ").slice(0, 100);
    const patched = {
      ...prev,
      last_message_preview: bodyPreview,
      last_message_at: msg.created_at,
      last_sender_id: msg.sender_id,
      unread: isMine ? (prev.unread || 0) : (prev.unread || 0) + 1,
    };
    // Move to top so the freshest chat sits above the fold.
    return [patched, ...rows.slice(0, idx), ...rows.slice(idx + 1)];
  });
}

// Adapter — keeps the shape { items, loading, error, refresh } that
// consumers rely on; renames `data` → `items` to match MessagesList's
// pre-existing variable name.
export function useConversations() {
  const { data, loading, error, refresh } = store.useData();
  return {
    items: data || [],
    loading,
    error,
    refresh,
  };
}
